import React, { useState, useEffect } from 'react'
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Badge,
  Tooltip,
  LinearProgress,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@material-ui/core'
import { Rating } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import {
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Photo as PhotoIcon,
  AccessTime as AccessTimeIcon,
  Flag as FlagIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon
} from '@material-ui/icons'
import { format, parseISO } from 'date-fns'
import axios from 'axios'
import { connect } from 'react-redux'
import BigImageComponent from '../Uploads/BigImageComponent'
import ReviewDetailModal from './ReviewDetailModal'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  filterBar: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  reviewCard: {
    marginBottom: theme.spacing(2),
    transition: 'all 0.3s',
    '&:hover': {
      boxShadow: theme.shadows[6]
    }
  },
  ratingSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  crewRating: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  lqaSection: {
    marginTop: theme.spacing(2)
  },
  lqaCategory: {
    marginBottom: theme.spacing(1)
  },
  issueChip: {
    margin: theme.spacing(0.5)
  },
  photoGrid: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1)
  },
  photo: {
    width: 100,
    height: 100,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer'
  },
  statusChip: {
    fontWeight: 'bold'
  },
  actionItem: {
    backgroundColor: theme.palette.warning.light,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1)
  },
  tabPanel: {
    paddingTop: theme.spacing(2)
  },
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(2)
  },
  emojiRating: {
    fontSize: '2rem',
    cursor: 'pointer'
  },
  progressBar: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  }
}))

const ReviewsDisplay = ({ url, user }) => {
  const classes = useStyles()
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [tabValue, setTabValue] = useState(0)
  const [filters, setFilters] = useState({
    type: 'all',
    dateRange: 'week',
    crew: 'all',
    minRating: 0
  })
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    crewReviews: 0,
    siteReviews: 0
  })
  const [selectedReview, setSelectedReview] = useState(null)
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false)
  const [selectedImages, setSelectedImages] = useState([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  useEffect(() => {
    fetchReviews()
  }, [filters])

  const fetchReviews = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${url}/api/reviews`, {
        params: filters
      })
      setReviews(response.data.reviews || [])
      setStats(response.data.stats || {})
    } catch (error) {
      console.error('Error fetching reviews:', error)
    } finally {
      setLoading(false)
    }
  }

  const getEmojiForRating = (rating) => {
    const emojiStyle = { fontSize: '2rem' }
    switch (rating) {
      case 3: return <span style={{ ...emojiStyle, color: '#4caf50' }}>😊</span>
      case 2: return <span style={{ ...emojiStyle, color: '#2196f3' }}>🙂</span>
      case 1: return <span style={{ ...emojiStyle, color: '#ff9800' }}>😐</span>
      case 0: return <span style={{ ...emojiStyle, color: '#f44336' }}>☹️</span>
      default: return <span style={{ ...emojiStyle, color: '#9e9e9e' }}>-</span>
    }
  }

  const getLQAScore = (lqaResponses) => {
    if (!lqaResponses) return { score: 0, total: 0 }
    let yesCount = 0
    let totalCount = 0
    
    Object.values(lqaResponses).forEach(category => {
      Object.values(category).forEach(response => {
        if (response !== 'na') {
          totalCount++
          if (response === 'yes') yesCount++
        }
      })
    })
    
    return {
      score: totalCount > 0 ? Math.round((yesCount / totalCount) * 100) : 0,
      total: totalCount
    }
  }

  const handleImageClick = (photos, index = 0) => {
    if (photos && photos.length > 0) {
      setSelectedImages(photos)
      setCurrentImageIndex(index)
      setImageGalleryOpen(true)
    }
  }

  const handleCloseImageGallery = () => {
    setImageGalleryOpen(false)
    setSelectedImages([])
    setCurrentImageIndex(0)
  }

  const handleReviewClick = (review) => {
    setSelectedReview(review)
  }

  const handleCloseReviewDetail = () => {
    setSelectedReview(null)
  }

  const renderReviewCard = (review) => {
    const lqaScore = getLQAScore(review.lqaResponses)
    const isUnified = review.crew?.onSite && review.crew?.ratings
    
    return (
      <Card key={review.reviewId || review._id} className={classes.reviewCard}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" component="div">
                  {review.site?.name || review.siteName}
                </Typography>
                <Chip
                  label={isUnified ? 'Site + Crew' : review.crew?.onSite === false ? 'Site Only' : 'Crew Only'}
                  color={isUnified ? 'primary' : 'default'}
                  size="small"
                  className={classes.statusChip}
                />
              </Box>

              <Typography variant="body2" color="textSecondary" gutterBottom>
                <LocationIcon fontSize="small" /> {review.site?.address || review.siteAddress}
              </Typography>

              {review.ratings && (
                <Box className={classes.ratingSection}>
                  <Rating value={review.ratings.overall} readOnly precision={0.5} />
                  <Typography variant="body2">
                    Overall: {review.ratings.overall}/5
                  </Typography>
                </Box>
              )}

              <Grid container spacing={1} style={{ marginTop: 8 }}>
                {review.ratings?.mowing && (
                  <Grid item>
                    <Chip label={`Mowing: ${review.ratings.mowing}/5`} size="small" />
                  </Grid>
                )}
                {review.ratings?.trimming && (
                  <Grid item>
                    <Chip label={`Trimming: ${review.ratings.trimming}/5`} size="small" />
                  </Grid>
                )}
                {review.ratings?.blowing && (
                  <Grid item>
                    <Chip label={`Blowing: ${review.ratings.blowing}/5`} size="small" />
                  </Grid>
                )}
                {review.ratings?.edging && (
                  <Grid item>
                    <Chip label={`Edging: ${review.ratings.edging}/5`} size="small" />
                  </Grid>
                )}
              </Grid>

              {review.crew?.ratings && (
                <Box className={classes.crewRating} mt={2}>
                  <Typography variant="subtitle2">
                    <GroupIcon fontSize="small" /> {review.crew.name}
                  </Typography>
                  <Box display="flex" gap={2}>
                    {review.crew.ratings.receptiveness !== undefined && (
                      <Tooltip title="Receptiveness">
                        <span>{getEmojiForRating(review.crew.ratings.receptiveness)}</span>
                      </Tooltip>
                    )}
                    {review.crew.ratings.onTask !== undefined && (
                      <Tooltip title="On Task">
                        <span>{getEmojiForRating(review.crew.ratings.onTask)}</span>
                      </Tooltip>
                    )}
                    {review.crew.ratings.attitude !== undefined && (
                      <Tooltip title="Attitude">
                        <span>{getEmojiForRating(review.crew.ratings.attitude)}</span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              )}

              {review.issues && review.issues.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Issues ({review.issues.length})
                  </Typography>
                  {review.issues.map((issue, idx) => (
                    <Chip
                      key={idx}
                      label={issue.title || issue.text}
                      color="secondary"
                      size="small"
                      className={classes.issueChip}
                      icon={<WarningIcon />}
                    />
                  ))}
                </Box>
              )}

              {review.actionItems && review.actionItems.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Action Items ({review.actionItems.length})
                  </Typography>
                  {review.actionItems.map((item, idx) => (
                    <Box key={idx} className={classes.actionItem}>
                      <Typography variant="body2">
                        <FlagIcon fontSize="small" color={item.priority === 'high' ? 'error' : 'inherit'} />
                        {' '}{item.text}
                      </Typography>
                      {item.dueDate && (
                        <Typography variant="caption" color="textSecondary">
                          Due: {format(parseISO(item.dueDate), 'MMM dd, yyyy')}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="caption" color="textSecondary">
                  <PersonIcon fontSize="small" /> Reviewed by: {review.reviewedByName}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  <AccessTimeIcon fontSize="small" /> {format(parseISO(review.submittedAt || review.createdAt), 'MMM dd, yyyy h:mm a')}
                </Typography>
                
                {lqaScore.total > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2">LQA Compliance</Typography>
                    <Box display="flex" alignItems="center">
                      <Box width="100%" mr={1}>
                        <LinearProgress
                          variant="determinate"
                          value={lqaScore.score}
                          className={classes.progressBar}
                          color={lqaScore.score >= 80 ? 'primary' : 'secondary'}
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        {lqaScore.score}%
                      </Typography>
                    </Box>
                  </Box>
                )}

                {review.photos && review.photos.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      <PhotoIcon fontSize="small" /> Photos ({review.photos.length})
                    </Typography>
                    <Box className={classes.photoGrid}>
                      {review.photos.slice(0, 4).map((photo, idx) => (
                        <img
                          key={idx}
                          src={photo.uri || photo.url}
                          alt={`Review photo ${idx + 1}`}
                          className={classes.photo}
                          onClick={() => handleImageClick(review.photos, idx)}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                      {review.photos.length > 4 && (
                        <Box
                          className={classes.photo}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          bgcolor="grey.200"
                          onClick={() => handleImageClick(review.photos, 4)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Typography>+{review.photos.length - 4}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    )
  }

  const renderLQADetails = (review) => {
    if (!review.lqaResponses || Object.keys(review.lqaResponses).length === 0) return null

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>LQA Compliance Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {Object.entries(review.lqaResponses).map(([category, responses]) => (
              <Grid item xs={12} md={6} key={category}>
                <Typography variant="subtitle2" gutterBottom>
                  {category.replace(/_/g, ' ')}
                </Typography>
                <List dense>
                  {Object.entries(responses).map(([question, answer]) => (
                    <ListItem key={question}>
                      <ListItemAvatar>
                        <Avatar style={{
                          backgroundColor: answer === 'yes' ? '#4caf50' : answer === 'no' ? '#f44336' : '#9e9e9e',
                          width: 24,
                          height: 24
                        }}>
                          {answer === 'yes' ? <CheckCircleIcon style={{ fontSize: 16 }} /> :
                           answer === 'no' ? <CancelIcon style={{ fontSize: 16 }} /> : '-'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={question.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        secondary={answer.toUpperCase()}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4">Site & Crew Reviews</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={fetchReviews}
        >
          Refresh
        </Button>
      </Box>

      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={6} md={3}>
          <Card className={classes.statCard}>
            <Typography variant="h3" color="primary">{stats.totalReviews}</Typography>
            <Typography variant="subtitle2">Total Reviews</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card className={classes.statCard}>
            <Rating value={stats.averageRating} readOnly precision={0.1} />
            <Typography variant="subtitle2">Average Rating</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card className={classes.statCard}>
            <Typography variant="h3" color="primary">{stats.siteReviews}</Typography>
            <Typography variant="subtitle2">Site Reviews</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card className={classes.statCard}>
            <Typography variant="h3" color="primary">{stats.crewReviews}</Typography>
            <Typography variant="subtitle2">Crew Reviews</Typography>
          </Card>
        </Grid>
      </Grid>

      <Paper className={classes.filterBar}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Review Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              >
                <MenuItem value="all">All Reviews</MenuItem>
                <MenuItem value="unified">Site + Crew</MenuItem>
                <MenuItem value="site">Site Only</MenuItem>
                <MenuItem value="crew">Crew Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={filters.dateRange}
                onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Minimum Rating</InputLabel>
              <Select
                value={filters.minRating}
                onChange={(e) => setFilters({ ...filters, minRating: e.target.value })}
              >
                <MenuItem value={0}>All Ratings</MenuItem>
                <MenuItem value={1}>1+ Stars</MenuItem>
                <MenuItem value={2}>2+ Stars</MenuItem>
                <MenuItem value={3}>3+ Stars</MenuItem>
                <MenuItem value={4}>4+ Stars</MenuItem>
                <MenuItem value={5}>5 Stars Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilters({ type: 'all', dateRange: 'week', crew: 'all', minRating: 0 })}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        style={{ marginBottom: 24 }}
      >
        <Tab label="All Reviews" />
        <Tab label="Pending Action" />
        <Tab label="Issues Reported" />
        <Tab label="High Performers" />
      </Tabs>

      <Box className={classes.tabPanel}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tabValue === 0 && reviews.map(review => renderReviewCard(review))}
            {tabValue === 1 && reviews
              .filter(r => r.actionItems && r.actionItems.length > 0)
              .map(review => renderReviewCard(review))}
            {tabValue === 2 && reviews
              .filter(r => r.issues && r.issues.length > 0)
              .map(review => renderReviewCard(review))}
            {tabValue === 3 && reviews
              .filter(r => r.ratings && r.ratings.overall >= 4)
              .map(review => renderReviewCard(review))}
          </>
        )}
      </Box>

      {/* Image Gallery Modal */}
      {imageGalleryOpen && (
        <BigImageComponent
          open={imageGalleryOpen}
          closeModal={handleCloseImageGallery}
          images={selectedImages.map(photo => ({
            url: photo.uri || photo.url,
            title: photo.title || 'Review Photo',
            _id: photo._id || photo.uri
          }))}
          currentImage={currentImageIndex}
        />
      )}

      {/* Review Detail Modal */}
      {selectedReview && (
        <ReviewDetailModal
          open={Boolean(selectedReview)}
          onClose={handleCloseReviewDetail}
          review={selectedReview}
          onImageClick={handleImageClick}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(ReviewsDisplay)